// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet, Platform,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii, Shadow } from '../theme/tokens';
import { useResponsive } from '../theme/responsive';
import { useApp, navigate } from '../store/AppStore';
import { useAuth } from '../store/AuthContext';
import { ScreenShell } from '../components/WebShell';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import PrimaryButton from '../components/Button_Component/Primary_Button';
import { Chip } from '../components/Chip';
import ConfirmDialog from '../components/ConfirmDialog';
import SuccessDialog from '../components/SuccessDialog';
import { Ticket, Comment, Delivery, DeliveryRow } from '../schema/types';
import { HqEvidencePanel } from '../components/HqEvidencePanel';
import { SlaDetail } from '../components/SlaState';
import { NodeLabel } from '../components/NodeLabel';
import { ticketsService } from '../services/tickets/tickets.service';
import { agentsService } from '../services/agents/agents.service';
import { apiClient } from '../services/api-client';
import { TICKETS, WATCHERS, PLATFORM_ORG } from '../api-endpoints/api-endpoints';
import { nextStatuses, statusMenuLabel } from '../lib/ticketStatus';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function ChannelIcon({ channel, size = 13 }: { channel: string; size?: number }) {
  const map: Record<string, string> = {
    email: 'Mail', api: 'Server', web: 'Globe', agent: 'AtSign',
  };
  return <Icon name={map[channel] ?? 'Globe'} size={size} strokeWidth={1.8} color={Colors.ink3} />;
}

// Exact, human timestamp from the API ISO string ("Jun 8, 2026, 3:45 PM").
function fullTimestamp(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function slaColor(min: number | null): string {
  if (min === null) return Colors.ink3;
  if (min < 0) return Colors.danger;
  if (min < 60) return Colors.warn;
  return Colors.ink3;
}

function slaText(min: number | null): string {
  if (min === null) return '—';
  if (min < 0) return `${Math.abs(min)}m over`;
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ─── DeliveryBadge ────────────────────────────────────────────────────────────
interface DeliveryBadgeProps {
  delivery: Delivery;
  expanded: boolean;
  onToggle: () => void;
}

function DeliveryBadge({ delivery, expanded, onToggle }: DeliveryBadgeProps) {
  const hasRows = !!(delivery.rows && delivery.rows.length > 0);

  let iconName: string;
  let label: string;
  let color: string;

  switch (delivery.state) {
    case 'queued':
      iconName = 'Clock'; label = 'Sending…'; color = Colors.ink4; break;
    case 'sent':
      iconName = 'Check';
      label = `Emailed ${delivery.n ?? delivery.sent ?? 1} recipient${(delivery.n ?? delivery.sent ?? 1) === 1 ? '' : 's'}`;
      color = Colors.success;
      break;
    case 'partial':
      iconName = 'Alert';
      label = `Emailed ${delivery.sent ?? 0} / ${delivery.total ?? 0}`;
      color = Colors.warn;
      break;
    case 'bounced':
      iconName = 'XCircle'; label = 'Delivery failed'; color = Colors.danger; break;
    default:
      iconName = 'Clock'; label = ''; color = Colors.ink4;
  }

  return (
    <TouchableOpacity
      style={styles.deliveryBadge}
      onPress={hasRows ? onToggle : undefined}
      activeOpacity={hasRows ? 0.7 : 1}
    >
      <Icon name={iconName} size={12} color={color} />
      <Text style={[styles.deliveryBadgeText, { color }]}>{label}</Text>
      {hasRows && (
        <View style={{ transform: [{ rotate: expanded ? '180deg' : '0deg' }] }}>
          <Icon name="Chevron" size={11} color={Colors.ink4} />
        </View>
      )}
    </TouchableOpacity>
  );
}

// ─── DeliveryPanel ────────────────────────────────────────────────────────────
interface DeliveryPanelProps {
  delivery: Delivery;
  onClose: () => void;
}

function DeliveryPanel({ delivery, onClose }: DeliveryPanelProps) {
  const sent = delivery.sent ?? 0;
  const total = delivery.total ?? (delivery.rows?.length ?? 0);
  const summary = delivery.state === 'sent'
    ? `Delivered to ${sent} recipient${sent !== 1 ? 's' : ''}`
    : delivery.state === 'partial'
    ? `${sent} of ${total} delivered`
    : 'All deliveries failed';

  return (
    <View style={styles.deliveryPanel}>
      <View style={styles.deliveryPanelHeader}>
        <Icon name="Chevron" size={11} color={Colors.ink3} style={{ transform: [{ rotate: '180deg' }] } as any} />
        <Text style={styles.deliveryPanelSummary}>{summary}</Text>
      </View>
      {(delivery.rows ?? []).map((row, i) => (
        <View key={i} style={styles.deliveryRow}>
          <Text style={styles.deliveryEmail} numberOfLines={1}>{row.email}</Text>
          {row.ok ? (
            <View style={styles.deliveryRowStatus}>
              <Icon name="Check" size={12} color={Colors.success} />
              <Text style={[styles.deliveryRowStatusText, { color: Colors.success }]}>
                Sent{row.time ? ` · ${row.time}` : ''}
              </Text>
            </View>
          ) : (
            <View style={styles.deliveryRowStatus}>
              <Icon name="XCircle" size={12} color={Colors.danger} />
              <Text style={[styles.deliveryRowStatusText, { color: Colors.danger }]}>
                Bounced{row.reason ? ` — ${row.reason}` : ''}
              </Text>
            </View>
          )}
        </View>
      ))}
      <View style={styles.deliveryPanelFooter}>
        <PrimaryButton title="Close" size="xs" variant="ghost" onPress={onClose} />
      </View>
    </View>
  );
}

// ─── Comment Renderers ────────────────────────────────────────────────────────
interface CommentItemProps {
  comment: Comment;
  ticket: Ticket;
  onToggleDelivery: (commentId: string) => void;
  currentUserId?: string;
  onEdit?: (comment: Comment) => void;
  onDelete?: (comment: Comment) => void;
}

// Inline edit/delete affordances shown on an agent's OWN messages. Premium web
// hover: a pencil + trash that tint on hover (trash → danger). Author-gated on
// the FE; the backend also enforces author-only.
function CommentActions({ comment, currentUserId, onEdit, onDelete }: {
  comment: Comment;
  currentUserId?: string;
  onEdit?: (c: Comment) => void;
  onDelete?: (c: Comment) => void;
}) {
  const [hover, setHover] = useState<'edit' | 'del' | null>(null);
  const canManage = !!onEdit && !!currentUserId && comment.authorPlatformUserId === currentUserId;
  if (!canManage) return null;

  if (Platform.OS !== 'web') {
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginLeft: 8 }}>
        <TouchableOpacity onPress={() => onEdit?.(comment)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }} accessibilityLabel="Edit message">
          <Icon name="Edit" size={13} color={Colors.ink4} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => onDelete?.(comment)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }} accessibilityLabel="Delete message">
          <Icon name="Trash" size={13} color={Colors.ink4} />
        </TouchableOpacity>
      </View>
    );
  }

  const btn = (active: boolean, danger?: boolean): any => ({
    display: 'grid', placeItems: 'center', width: 26, height: 26, padding: 0,
    border: 'none', cursor: 'pointer', borderRadius: 7, marginLeft: 2,
    background: active ? (danger ? 'var(--danger-soft)' : 'var(--paper-2)') : 'transparent',
    transition: 'background-color 130ms ease, transform 130ms cubic-bezier(.34,1.56,.64,1)',
    transform: active ? 'scale(1.08)' : 'scale(1)',
  });
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 8 }}>
      <button
        onClick={() => onEdit?.(comment)} aria-label="Edit message"
        onMouseEnter={() => setHover('edit')} onMouseLeave={() => setHover(null)}
        style={btn(hover === 'edit')}
      >
        <Icon name="Edit" size={13} color={hover === 'edit' ? Colors.accent : Colors.ink4} />
      </button>
      <button
        onClick={() => onDelete?.(comment)} aria-label="Delete message"
        onMouseEnter={() => setHover('del')} onMouseLeave={() => setHover(null)}
        style={btn(hover === 'del', true)}
      >
        <Icon name="Trash" size={13} color={hover === 'del' ? Colors.danger : Colors.ink4} />
      </button>
    </div>
  );
}

function CommentItem({ comment, ticket, onToggleDelivery, currentUserId, onEdit, onDelete }: CommentItemProps) {
  if (comment.type === 'customer') {
    return (
      <View style={styles.commentCustomer}>
        <View style={styles.commentHeader}>
          <Avatar initials={ticket.customer.initials} color={ticket.customer.color} size="md" />
          <Text style={styles.commentAuthor}>{comment.author}</Text>
          <Text style={styles.commentMeta}>· Customer</Text>
          {comment.viaEmail && (
            <>
              <Text style={styles.commentMeta}> · </Text>
              <Icon name="Mail" size={11} color={Colors.ink4} />
              <Text style={[styles.commentMeta, styles.italic]}> via email</Text>
            </>
          )}
          <View style={{ flex: 1 }} />
          <Text style={styles.commentTime}>{comment.time}</Text>
        </View>
        <Text style={styles.commentBody}>{comment.body}</Text>
      </View>
    );
  }

  if (comment.type === 'public') {
    return (
      <View style={[styles.commentAgent, Shadow.sm as any]}>
        <View style={styles.commentHeader}>
          <Avatar
            initials={agentInitials(comment.author || 'Agent')}
            color={gradientFor(comment.author || 'agent')}
            size="md"
          />
          <Text style={styles.commentAuthor}>{comment.author}</Text>
          <Text style={styles.commentMeta}>· Agent</Text>
          {comment.editedAt && <Text style={[styles.commentMeta, styles.italic]}> · edited</Text>}
          <View style={{ flex: 1 }} />
          <Text style={styles.commentTime}>{comment.time}</Text>
          <CommentActions comment={comment} currentUserId={currentUserId} onEdit={onEdit} onDelete={onDelete} />
        </View>
        <Text style={[styles.commentBody, { marginBottom: 12 }]}>{comment.body}</Text>
        <View style={styles.commentFooter}>
          <Chip label="Public · visible to customer" tone="info" dot size="sm" />
          <View style={{ flex: 1 }} />
          {comment.delivery && (
            <DeliveryBadge
              delivery={comment.delivery}
              expanded={!!comment._expanded}
              onToggle={() => onToggleDelivery(comment.id)}
            />
          )}
        </View>
        {comment._expanded && comment.delivery && (
          <DeliveryPanel
            delivery={comment.delivery}
            onClose={() => onToggleDelivery(comment.id)}
          />
        )}
      </View>
    );
  }

  if (comment.type === 'internal') {
    return (
      <View style={styles.commentInternal}>
        <View style={styles.commentHeader}>
          <Avatar
            initials={agentInitials(comment.author || 'Agent')}
            color={gradientFor(comment.author || 'agent')}
            size="md"
          />
          <Text style={styles.commentAuthor}>{comment.author}</Text>
          <Text style={styles.commentMeta}>· Agent</Text>
          {comment.editedAt && <Text style={[styles.commentMeta, styles.italic]}> · edited</Text>}
          <View style={{ flex: 1 }} />
          <Text style={styles.commentTime}>{comment.time}</Text>
          <CommentActions comment={comment} currentUserId={currentUserId} onEdit={onEdit} onDelete={onDelete} />
        </View>
        <Text style={[styles.commentBody, { marginBottom: 10 }]}>{comment.body}</Text>
        <Chip label="Internal · agents only" tone="warn" dot size="sm" />
      </View>
    );
  }

  if (comment.type === 'system-bounce') {
    return (
      <View style={styles.commentBounce}>
        <View style={styles.commentHeader}>
          <View style={styles.bounceIconWrap}>
            <Icon name="Alert" size={13} color={Colors.white} />
          </View>
          <Text style={styles.commentAuthor}>System</Text>
          <Chip label="Internal" tone="warn" dot size="sm" style={{ marginLeft: 4 }} />
          <View style={{ flex: 1 }} />
          <Text style={styles.commentTime}>{comment.time}</Text>
        </View>
        <View style={{ marginTop: 6 }}>
          <Text style={styles.bounceBody}>
            {'Email delivery failed for '}
            <Text style={styles.bounceCode}>{comment.failedEmail}</Text>
          </Text>
          {comment.reason && (
            <Text style={styles.bounceReason}>Reason: {comment.reason}</Text>
          )}
          {comment.okEmail && (
            <Text style={styles.bounceOk}>
              {'The customer email '}
              <Text style={styles.bounceCode}>{comment.okEmail}</Text>
              {' was delivered successfully.'}
            </Text>
          )}
        </View>
      </View>
    );
  }

  return null;
}

// ─── Opening request ──────────────────────────────────────────────────────────
// The customer's original problem statement, surfaced as the first item in the
// thread so the conversation always opens with context (not an empty void).
function OpeningRequest({ ticket }: { ticket: Ticket }) {
  return (
    <View style={styles.openingCard}>
      <View style={styles.openingAccent} />
      <View style={styles.commentHeader}>
        <Avatar initials={ticket.customer.initials} color={ticket.customer.color} size="md" />
        <Text style={styles.commentAuthor}>{ticket.customer.name}</Text>
        <View style={styles.openingTag}>
          <Text style={styles.openingTagText}>Original request</Text>
        </View>
        <View style={{ flex: 1 }} />
        <Text style={styles.commentTime}>{fullTimestamp(ticket.createdAtISO) || ticket.openedAgo}</Text>
      </View>
      <Text style={styles.openingSubject}>{ticket.subject}</Text>

      {/*
        SKTICK-DB-001 Surface B — the evidence panel sits ABOVE the description
        on an HQ-raised ticket, because "why this was raised" is the context
        the description is read against. It renders nothing for every other
        source, so the existing custom-fields rendering is untouched.
      */}
      <HqEvidencePanel ticket={ticket as any} style={{ marginTop: 10 }} />

      {!!ticket.description && (
        <Text style={[styles.commentBody, { marginTop: 6 }]}>{ticket.description}</Text>
      )}
    </View>
  );
}

// Graceful, centered state for a thread with no replies yet.
function ThreadEmpty({ ticket }: { ticket: Ticket }) {
  return (
    <View style={styles.threadEmpty}>
      <View style={styles.threadEmptyIcon}>
        <Icon name="Reply" size={22} color={Colors.accent} stroke={1.8} />
      </View>
      <Text style={styles.threadEmptyTitle}>Awaiting the first reply</Text>
      <Text style={styles.threadEmptySub}>
        This conversation is just getting started. Use the composer below to respond to {ticket.customer.name}.
      </Text>
    </View>
  );
}

// ─── Right Panel ──────────────────────────────────────────────────────────────
function PropRow({ label, children, z }: { label: string; children: React.ReactNode; z?: number }) {
  // `z` lifts a row (and its absolute dropdown) above the rows below it so an
  // open picker overlays cleanly instead of being painted over.
  return (
    <View style={[styles.propRow, z ? ({ zIndex: z } as any) : null]}>
      <Text style={styles.propLabel}>{label}</Text>
      <View style={styles.propValue}>{children}</View>
    </View>
  );
}

interface RightPanelProps {
  ticket: Ticket;
  onReload: () => void;
}

// A single watcher chip — mirrors TagChip's premium web hover (lift + springy
// danger halo on the remove button), with the teammate's avatar inline.
function WatcherChip({ name, pid, onDelete }: { name: string; pid: string; onDelete: () => void }) {
  const [hover, setHover] = useState(false);
  const [delHover, setDelHover] = useState(false);

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.tagChip}>
        <Avatar initials={agentInitials(name)} color={gradientFor(pid)} size="sm" />
        <Text style={styles.tagChipText} numberOfLines={1}>{name}</Text>
        <TouchableOpacity
          onPress={onDelete}
          hitSlop={{ top: 6, bottom: 6, left: 2, right: 6 }}
          accessibilityLabel={`Remove watcher ${name}`}
        >
          <Icon name="Trash" size={11} color={Colors.ink4} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setDelHover(false); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
        height: 28, paddingLeft: 5, paddingRight: 5, boxSizing: 'border-box',
        borderRadius: 999, border: '1px solid',
        borderColor: hover ? 'var(--line-strong)' : 'var(--line)',
        background: hover
          ? 'linear-gradient(180deg, var(--card) 0%, var(--paper-2) 130%)'
          : 'var(--card)',
        boxShadow: hover ? 'var(--shadow)' : 'var(--shadow-sm)',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'transform 170ms cubic-bezier(.2,.8,.2,1), box-shadow 170ms ease, background-color 150ms ease, border-color 150ms ease',
      }}
    >
      <Avatar initials={agentInitials(name)} color={gradientFor(pid)} size="sm" />
      <span style={{
        fontSize: 12, fontWeight: 500, color: 'var(--ink-2)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130,
      }}>{name}</span>
      <button
        onClick={onDelete}
        onMouseEnter={() => setDelHover(true)}
        onMouseLeave={() => setDelHover(false)}
        aria-label={`Remove watcher ${name}`}
        style={{
          display: 'grid', placeItems: 'center', width: 20, height: 20, padding: 0,
          border: 'none', cursor: 'pointer', borderRadius: 999,
          background: delHover ? 'var(--danger-soft)' : 'transparent',
          transform: delHover ? 'scale(1.14)' : 'scale(1)',
          transition: 'background-color 140ms ease, transform 140ms cubic-bezier(.34,1.56,.64,1)',
        }}
      >
        <Icon name="Trash" size={11} color={delHover ? Colors.danger : Colors.ink4} />
      </button>
    </div>
  );
}

// Inline watcher picker — a searchable teammate combobox anchored under the
// "+ Add" pill. Teammates already watching float to the top (✓, click to remove);
// the rest show a + to add. Everything happens in place, no modal.
function WatcherPickerDropdown({
  pos, query, onQueryChange, items, busy, inputRef, currentUserId, onPick, onClose,
}: {
  pos: { top: number; left: number } | null;
  query: string;
  onQueryChange: (v: string) => void;
  items: { platformUserId: string; name: string; email?: string; watching: boolean }[];
  busy: boolean;
  inputRef: React.RefObject<any>;
  currentUserId?: string;
  onPick: (it: { platformUserId: string; name: string; watching: boolean }) => void;
  onClose: () => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);

  const menu = (
    <div
      data-dd-keep-watch
      style={{
        position: 'fixed', top: pos ? pos.top : 0, left: pos ? pos.left : 0, zIndex: 4000,
        width: 268, background: 'var(--card)', border: '1px solid var(--line-strong)',
        borderRadius: 12, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
        transformOrigin: 'top left',
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.97)',
        transition: 'opacity 150ms ease, transform 170ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--paper-2)' }}>
        <span style={{ display: 'grid', placeItems: 'center', flexShrink: 0, paddingLeft: 2 }}>
          <Icon name="Search" size={13} color={Colors.ink4} />
        </span>
        <input
          ref={inputRef}
          autoFocus
          value={query}
          onChange={(e: any) => onQueryChange(e.target.value)}
          onKeyDown={(e: any) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }}
          placeholder="Search teammates…"
          style={{
            flex: 1, minWidth: 0, height: 26, padding: 0, border: 'none', outline: 'none',
            fontSize: 13, background: 'transparent', color: 'var(--ink)',
          }}
        />
      </div>

      <div style={{ maxHeight: 264, overflowY: 'auto', padding: 4 }}>
        {items.map(it => {
          const isHover = hover === it.platformUserId;
          const applied = it.watching;
          return (
            <button
              key={it.platformUserId}
              disabled={busy}
              onMouseEnter={() => setHover(it.platformUserId)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onPick(it)}
              title={applied ? 'Remove watcher' : 'Add watcher'}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '7px 10px', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                background: isHover
                  ? (applied ? 'var(--danger-soft)' : 'var(--paper-2)')
                  : (applied ? 'var(--accent-soft)' : 'transparent'),
                transition: 'background-color 120ms ease',
              }}
            >
              <Avatar initials={agentInitials(it.name)} color={gradientFor(it.platformUserId)} size="sm" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: applied ? 600 : 500, color: applied ? 'var(--accent-ink)' : 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.name}{it.platformUserId === currentUserId ? ' (you)' : ''}
                </span>
                {it.email ? <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.email}</span> : null}
              </span>
              {applied
                ? <Icon name={isHover ? 'X' : 'Check'} size={14} color={isHover ? Colors.danger : Colors.accent} stroke={2.4} />
                : <Icon name="Plus" size={13} color={isHover ? Colors.accent : Colors.ink4} stroke={2.2} />}
            </button>
          );
        })}

        {items.length === 0 && (
          <div style={{ padding: '12px', fontSize: 12.5, color: 'var(--ink-4)', textAlign: 'center' }}>
            {query.trim() ? 'No matching teammates' : 'No teammates to add'}
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(menu, document.body)
    : menu;
}

function WatchersRow({ ticket, onReload }: RightPanelProps) {
  const { toast, agentMap } = useApp();
  const { user, org } = useAuth();
  const apiId = (ticket as any)._apiId;

  const [watchers, setWatchers] = useState<Array<{ platformUserId: string; name: string }>>([]);
  const [picker, setPicker] = useState(false); // native-only add-watcher card
  const [candidates, setCandidates] = useState<any[]>([]); // full teammate pool
  const [deleteTarget, setDeleteTarget] = useState<{ platformUserId: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  // Inline picker dropdown (web).
  const [showDD, setShowDD] = useState(false);
  const [wQuery, setWQuery] = useState('');
  const [ddPos, setDDPos] = useState<{ top: number; left: number } | null>(null);
  const wInputRef = useRef<any>(null);

  const nameFor = (pid: string) => agentMap[pid] || pid;

  // Load the real watcher rows (keyed by platformUserId) and resolve names.
  const loadWatchers = async () => {
    if (!apiId) return;
    try {
      const res: any = await apiClient.get(WATCHERS.list(apiId));
      const rows = res?.data || [];
      setWatchers(rows.map((w: any) => ({ platformUserId: w.platformUserId, name: nameFor(w.platformUserId) })));
    } catch { /* leave list as-is on failure */ }
  };

  useEffect(() => { loadWatchers(); /* eslint-disable-next-line */ }, [apiId, agentMap]);

  // Close the inline picker on outside-click (web). The button + dropdown both
  // sit inside a `data-dd-keep` subtree, so clicks within them are ignored.
  useEffect(() => {
    if (Platform.OS !== 'web' || !showDD) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && typeof t.closest === 'function' && t.closest('[data-dd-keep-watch]')) return;
      setShowDD(false); setWQuery('');
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [showDD]);

  // Load the FULL teammate pool — registered agents ∪ VERIFIED org members ∪ the
  // current user. "Verified" = an active membership (the invite was accepted);
  // pending/invited teammates are excluded so you can only watch real agents.
  // We DON'T filter out current watchers here; the render derives each row's
  // `watching` flag from live state, so toggling stays consistent without
  // re-fetching the pool.
  const loadCandidates = async () => {
    const [agentsRes, membersRes] = await Promise.allSettled([
      agentsService.list(),
      org?.id ? apiClient.get(PLATFORM_ORG.members(org.id)) : Promise.resolve(null),
    ]);
    let agents: any[] = [];
    if (agentsRes.status === 'fulfilled') {
      const raw = (agentsRes.value as any)?.data || agentsRes.value || [];
      agents = Array.isArray(raw) ? raw : [];
    }
    const haveIds = new Set(agents.map((a: any) => a.platformUserId).filter(Boolean));
    let members: any[] = [];
    if (membersRes.status === 'fulfilled' && membersRes.value) {
      const raw = (membersRes.value as any)?.data || membersRes.value || [];
      members = (Array.isArray(raw) ? raw : [])
        // Verified-only: drop anyone whose membership isn't active (pending/invited).
        .filter((m: any) => m.status === 'active' && m.user_id && !haveIds.has(m.user_id))
        .map((m: any) => ({
          platformUserId: m.user_id,
          displayName: [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ').trim() || (m.user?.email || '').split('@')[0] || m.user_id,
          email: m.user?.email,
        }));
    }
    let list = [...agents, ...members];
    if (user?.id && !list.some((a: any) => a.platformUserId === user.id)) {
      list = [{ platformUserId: user.id, displayName: ([user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || 'Me') + ' (me)' }, ...list];
    }
    setCandidates(list.filter((a: any) => a.platformUserId));
  };

  const openPicker = async () => {
    setPicker(true);
    await loadCandidates();
  };

  // Inline add/remove from the dropdown — no confirm, picker stays open.
  const toggleWatcher = async (item: { platformUserId: string; name: string; watching: boolean }) => {
    setBusy(true);
    try {
      if (item.watching) {
        await apiClient.delete(WATCHERS.remove(apiId, item.platformUserId));
      } else {
        await apiClient.post(WATCHERS.add(apiId), { platformUserId: item.platformUserId });
      }
      await loadWatchers();
      onReload();
      if (Platform.OS === 'web' && wInputRef.current?.focus) wInputRef.current.focus();
    } catch (e: any) {
      const msg = e?.response?.status === 409
        ? 'Already watching this ticket.'
        : (e?.response?.data?.message || (item.watching ? 'Failed to remove watcher.' : 'Failed to add watcher.'));
      toast(msg, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const addWatcher = async (agent: any) => {
    setBusy(true);
    try {
      await apiClient.post(WATCHERS.add(apiId), { platformUserId: agent.platformUserId });
      setPicker(false);
      await loadWatchers();
      onReload();
      setSuccess(`${(agent.displayName || agent.name || 'User').replace(/\s*\(me\)$/, '')} is now watching this ticket.`);
    } catch (e: any) {
      const msg = e?.response?.status === 409 ? 'Already watching this ticket.' : (e?.response?.data?.message || 'Failed to add watcher.');
      toast(msg, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await apiClient.delete(WATCHERS.remove(apiId, deleteTarget.platformUserId));
      const removed = deleteTarget.name;
      setDeleteTarget(null);
      await loadWatchers();
      onReload();
      setSuccess(`${removed} removed from watchers.`);
    } catch {
      toast('Failed to remove watcher.', 'danger');
    } finally {
      setBusy(false);
    }
  };

  // Combined picker rows: the full teammate pool with a live `watching` flag,
  // plus any watcher not present in the pool (e.g. a deactivated member), so
  // currently-watching teammates always show — and can be removed — in the list.
  const watchingSet = new Set(watchers.map(w => w.platformUserId));
  const wq = wQuery.trim().toLowerCase();
  const ddItems = (() => {
    const rows = candidates.map((a: any) => ({
      platformUserId: a.platformUserId,
      name: (a.displayName || a.name || a.platformUserId || 'Agent').replace(/\s*\(me\)$/, ''),
      email: a.email as string | undefined,
      watching: watchingSet.has(a.platformUserId),
    }));
    watchers.forEach(w => {
      if (!rows.some(r => r.platformUserId === w.platformUserId)) {
        rows.push({ platformUserId: w.platformUserId, name: w.name, email: undefined, watching: true });
      }
    });
    return rows
      .filter(r => !wq || r.name.toLowerCase().includes(wq) || (r.email || '').toLowerCase().includes(wq))
      .sort((a, b) => (Number(b.watching) - Number(a.watching)) || a.name.localeCompare(b.name))
      .slice(0, 30);
  })();

  return (
    <View style={{ width: '100%', minWidth: 0 }}>
      <View style={styles.tagsRow}>
        {watchers.length === 0 && (
          <Text style={[styles.panelPropText, { color: Colors.ink4 }]}>None</Text>
        )}
        {watchers.map(w => (
          <WatcherChip
            key={w.platformUserId}
            name={w.name}
            pid={w.platformUserId}
            onDelete={() => setDeleteTarget(w)}
          />
        ))}
        {Platform.OS === 'web' ? (
          <div data-dd-keep-watch style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              onClick={(e: any) => {
                if (showDD) { setShowDD(false); setWQuery(''); return; }
                const r = e.currentTarget.getBoundingClientRect();
                const W = 268;
                const left = Math.max(8, Math.min(r.left, window.innerWidth - W - 8));
                setDDPos({ top: r.bottom + 8, left });
                setWQuery('');
                loadCandidates();
                setShowDD(true);
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, height: 28,
                paddingLeft: 12, paddingRight: 13, border: '1px solid var(--accent-line)',
                borderRadius: 999, cursor: 'pointer', boxShadow: 'var(--shadow-sm)',
                background: showDD ? 'var(--accent-line)' : 'var(--accent-soft)',
                color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600, letterSpacing: 0.1,
                transition: 'background-color 140ms ease, box-shadow 140ms ease',
              }}
            >
              <Icon name="Plus" size={11} color={Colors.accent} stroke={2.4} />
              Add
            </button>
            {showDD && (
              <WatcherPickerDropdown
                pos={ddPos}
                query={wQuery}
                onQueryChange={setWQuery}
                items={ddItems}
                busy={busy}
                inputRef={wInputRef}
                currentUserId={user?.id}
                onPick={toggleWatcher}
                onClose={() => { setShowDD(false); setWQuery(''); }}
              />
            )}
          </div>
        ) : (
          <TouchableOpacity style={styles.tagAddBtn} onPress={openPicker}>
            <Icon name="Plus" size={11} color={Colors.accent} stroke={2.4} />
            <Text style={styles.tagAddText}>Add</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Add-watcher picker card */}
      <ConfirmDialog
        visible={picker}
        title="Add watcher"
        message="Choose a teammate to watch this ticket — they'll get updates on activity."
        confirmText="Done"
        cancelText="Close"
        confirmColor={Colors.accent}
        onConfirm={() => setPicker(false)}
        onCancel={() => setPicker(false)}
        onClose={() => setPicker(false)}
      >
        <View style={{ maxHeight: 260, marginTop: 6 }}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {candidates.filter((a: any) => !watchingSet.has(a.platformUserId)).length === 0 ? (
              <Text style={{ fontSize: 12.5, color: Colors.ink4, paddingVertical: 10 }}>No more teammates to add.</Text>
            ) : candidates.filter((a: any) => !watchingSet.has(a.platformUserId)).map((a: any) => {
              const name = (a.displayName || a.name || a.platformUserId || 'Agent').replace(/\s*\(me\)$/, '');
              return (
                <TouchableOpacity
                  key={a.platformUserId}
                  style={styles.watcherPickRow}
                  onPress={() => addWatcher(a)}
                  disabled={busy}
                  activeOpacity={0.7}
                >
                  <Avatar initials={agentInitials(name)} color={gradientFor(a.platformUserId)} size="sm" />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.watcherPickName} numberOfLines={1}>
                      {name}{a.platformUserId === user?.id ? ' (you)' : ''}
                    </Text>
                    {a.email ? <Text style={styles.watcherPickEmail} numberOfLines={1}>{a.email}</Text> : null}
                  </View>
                  <Icon name="Plus" size={15} color={Colors.accent} stroke={2.4} />
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </ConfirmDialog>

      {/* Delete → confirm */}
      <ConfirmDialog
        visible={deleteTarget !== null}
        title="Remove watcher?"
        message={deleteTarget ? `${deleteTarget.name} will stop receiving updates for this ticket.` : ''}
        confirmText="Remove"
        cancelText="Cancel"
        loading={busy}
        onConfirm={confirmDelete}
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
      />

      {/* Success */}
      <SuccessDialog
        visible={success !== null}
        title="Done"
        message={success ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setSuccess(null) }}
        onClose={() => setSuccess(null)}
      />
    </View>
  );
}

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

// ─── Assignee menu (premium) ────────────────────────────────────────────────
const AGENT_GRADIENTS = [
  'linear-gradient(135deg,#0ea5e9,#6366f1)',
  'linear-gradient(135deg,#f97316,#db2777)',
  'linear-gradient(135deg,#10b981,#0ea5e9)',
  'linear-gradient(135deg,#a855f7,#ec4899)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)',
  'linear-gradient(135deg,#84cc16,#22c55e)',
  'linear-gradient(135deg,#f59e0b,#dc2626)',
];
function gradientFor(str: string): string {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return AGENT_GRADIENTS[Math.abs(h) % AGENT_GRADIENTS.length];
}
function agentInitials(name: string): string {
  return (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
}
const CheckMark = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function AssigneeMenu({ agents, currentAssigneeId, currentUserId, onPick, onUnassign, pos }: {
  agents: any[]; currentAssigneeId?: string | null; currentUserId?: string | null;
  onPick: (a: any) => void; onUnassign: () => void;
  pos?: { top: number; right: number } | null;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const row: any = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '8px 10px', background: 'none', border: 'none', cursor: 'pointer',
    textAlign: 'left', borderRadius: 9, transition: 'background 120ms ease',
  };
  const avatar = (bg: string): any => ({
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
    display: 'grid', placeItems: 'center', color: '#fff', fontSize: 10.5, fontWeight: 700,
    background: bg, border: '1px solid rgba(0,0,0,0.06)', letterSpacing: '-0.2px',
  });
  const menu = (
    <div data-dd-keep style={{
      // Portaled to <body> and position:fixed so it escapes the right panel's
      // overflow/transform clipping and floats over the whole window — full
      // names/emails stay visible regardless of the panel width.
      position: 'fixed',
      top: pos ? pos.top : 0,
      right: pos ? pos.right : 16,
      zIndex: 2000,
      background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 14,
      boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22), 0 2px 6px rgba(10,10,10,0.06)',
      width: 300, maxWidth: '92vw', padding: 6, maxHeight: 332, overflowY: 'auto',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--ink-4)', textTransform: 'uppercase', padding: '6px 10px 4px' }}>
        Assign to
      </div>
      <button style={{ ...row, background: hover === '__unassign' ? 'var(--paper-2)' : 'none' }}
        onMouseEnter={() => setHover('__unassign')} onMouseLeave={() => setHover(null)} onClick={onUnassign}>
        <span style={{ ...avatar('transparent'), color: 'var(--ink-4)', border: '1px dashed var(--line-strong)' }}>—</span>
        <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)' }}>Unassigned</span>
        {!currentAssigneeId && <CheckMark />}
      </button>
      {agents.map((a: any) => {
        const id = a.platformUserId || a.id;
        const name = (a.displayName || a.name || a.platformUserId || 'Agent').replace(/\s*\(me\)$/, '');
        const isMe = id && id === currentUserId;
        const selected = id && id === currentAssigneeId;
        return (
          <button key={id} style={{ ...row, background: hover === id ? 'var(--paper-2)' : 'none' }}
            onMouseEnter={() => setHover(id)} onMouseLeave={() => setHover(null)} onClick={() => onPick(a)}>
            <span style={avatar(gradientFor(id || name))}>{agentInitials(name)}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name}{isMe ? ' (you)' : ''}
              </span>
              {a.email ? (
                <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.email}</span>
              ) : null}
            </span>
            {selected ? <CheckMark /> : null}
          </button>
        );
      })}
    </div>
  );
  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(menu, document.body)
    : menu;
}

// ─── More (⋯) menu — portaled so it never clips behind the header/thread ──────
function MoreMenu({ pos, onClose, ticket }: {
  pos?: { top: number; right: number } | null;
  onClose: () => void;
  ticket: Ticket;
}) {
  const { toast } = useApp();
  const [hover, setHover] = useState<string | null>(null);
  const items = [
    { key: 'merge', label: 'Merge ticket', action: () => toast('Select a target ticket to merge into.', 'info') },
    { key: 'time', label: 'Time tracking', action: () => toast('Time tracking is available via API.', 'info') },
    { key: 'print', label: 'Print / export', action: () => { (window as any).print(); } },
  ];
  const menu = (
    <div data-dd-keep style={{
      position: 'fixed',
      top: pos ? pos.top : 60,
      right: pos ? pos.right : 16,
      zIndex: 2000,
      background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 14,
      boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22), 0 2px 6px rgba(10,10,10,0.06)',
      minWidth: 200, padding: 6,
    }}>
      {items.map(item => (
        <button
          key={item.key}
          onClick={() => { onClose(); item.action(); }}
          onMouseEnter={() => setHover(item.key)}
          onMouseLeave={() => setHover(null)}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '9px 12px', fontSize: 13, color: 'var(--ink)',
            background: hover === item.key ? 'var(--paper-2)' : 'none',
            border: 'none', borderRadius: 9, cursor: 'pointer',
            transition: 'background 120ms ease',
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(menu, document.body)
    : menu;
}

// A single tag chip. On web it gets a premium hover: the chip lifts slightly
// and the delete affordance grows a soft danger halo + a springy pop, with the
// trash icon shifting from muted grey to danger red.
function TagChip({ tag, onDelete }: { tag: string; onDelete: () => void }) {
  const [hover, setHover] = useState(false);
  const [delHover, setDelHover] = useState(false);

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.tagChip}>
        <Text style={styles.tagChipText} numberOfLines={1}>{tag}</Text>
        <TouchableOpacity
          onPress={onDelete}
          hitSlop={{ top: 6, bottom: 6, left: 4, right: 6 }}
          accessibilityLabel={`Delete tag ${tag}`}
        >
          <Icon name="Trash" size={11} color={Colors.ink4} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setDelHover(false); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
        height: 28, paddingLeft: 11, paddingRight: 5, boxSizing: 'border-box',
        borderRadius: 999, border: '1px solid',
        borderColor: hover ? 'var(--line-strong)' : 'var(--line)',
        background: hover
          ? 'linear-gradient(180deg, var(--card) 0%, var(--paper-2) 130%)'
          : 'var(--card)',
        boxShadow: hover ? 'var(--shadow)' : 'var(--shadow-sm)',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'transform 170ms cubic-bezier(.2,.8,.2,1), box-shadow 170ms ease, background-color 150ms ease, border-color 150ms ease',
      }}
    >
      <span style={{
        fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', letterSpacing: 0.1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150,
      }}>{tag}</span>
      <button
        onClick={onDelete}
        onMouseEnter={() => setDelHover(true)}
        onMouseLeave={() => setDelHover(false)}
        aria-label={`Delete tag ${tag}`}
        style={{
          display: 'grid', placeItems: 'center', width: 20, height: 20, padding: 0,
          border: 'none', cursor: 'pointer', borderRadius: 999,
          background: delHover ? 'var(--danger-soft)' : 'transparent',
          transform: delHover ? 'scale(1.14)' : 'scale(1)',
          transition: 'background-color 140ms ease, transform 140ms cubic-bezier(.34,1.56,.64,1)',
        }}
      >
        <Icon name="Trash" size={11} color={delHover ? Colors.danger : Colors.ink4} />
      </button>
    </div>
  );
}

// Inline tag picker — a searchable combobox anchored under the "+ Add" pill.
// Type to filter existing tags; if the text matches nothing, an "Add …" row
// creates it. Everything happens in place, so the user never leaves the ticket.
function TagPickerDropdown({
  pos, query, onQueryChange, items, canCreate, busy, inputRef, onPick, onClose,
}: {
  pos: { top: number; left: number } | null;
  query: string;
  onQueryChange: (v: string) => void;
  items: { tag: string; applied: boolean }[];
  canCreate: boolean;
  busy: boolean;
  inputRef: React.RefObject<any>;
  onPick: (v: string) => void;
  onClose: () => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);

  const itemBase: any = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '8px 10px', border: 'none', borderRadius: 8, cursor: 'pointer',
    textAlign: 'left', fontSize: 13, color: 'var(--ink)',
    transition: 'background-color 120ms ease',
  };

  const menu = (
    <div
      data-dd-keep
      style={{
        position: 'fixed', top: pos ? pos.top : 0, left: pos ? pos.left : 0, zIndex: 4000,
        width: 248, background: 'var(--card)', border: '1px solid var(--line-strong)',
        borderRadius: 12, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
        transformOrigin: 'top left',
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.97)',
        transition: 'opacity 150ms ease, transform 170ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--paper-2)' }}>
        <span style={{ display: 'grid', placeItems: 'center', flexShrink: 0, paddingLeft: 2 }}>
          <Icon name="Search" size={13} color={Colors.ink4} />
        </span>
        <input
          ref={inputRef}
          autoFocus
          value={query}
          onChange={(e: any) => onQueryChange(e.target.value)}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter') { e.preventDefault(); if (query.trim()) onPick(query); }
            else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
          }}
          placeholder="Search or create a tag…"
          style={{
            flex: 1, minWidth: 0, height: 26, padding: 0, border: 'none', outline: 'none',
            fontSize: 13, background: 'transparent', color: 'var(--ink)',
          }}
        />
      </div>

      <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4 }}>
        {items.map(({ tag, applied }) => {
          const isHover = hover === tag;
          return (
            <button
              key={tag}
              disabled={busy}
              onMouseEnter={() => setHover(tag)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onPick(tag)}
              title={applied ? 'Remove from ticket' : 'Add to ticket'}
              style={{
                ...itemBase,
                color: applied ? 'var(--accent-ink)' : 'var(--ink)',
                fontWeight: applied ? 600 : 400,
                background: isHover
                  ? (applied ? 'var(--danger-soft)' : 'var(--paper-2)')
                  : (applied ? 'var(--accent-soft)' : 'transparent'),
              }}
            >
              <Icon name="Tag" size={12} color={applied ? Colors.accent : Colors.ink4} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tag}</span>
              {applied
                ? <Icon name={isHover ? 'X' : 'Check'} size={13} color={isHover ? Colors.danger : Colors.accent} stroke={2.4} />
                : <Icon name="Plus" size={12} color={isHover ? Colors.accent : Colors.ink4} stroke={2.2} />}
            </button>
          );
        })}

        {canCreate && (
          <button
            disabled={busy}
            onMouseEnter={() => setHover('__create')}
            onMouseLeave={() => setHover(null)}
            onClick={() => onPick(query)}
            style={{
              ...itemBase, fontWeight: 600, color: 'var(--accent-ink)',
              background: hover === '__create' ? 'var(--accent-soft)' : 'transparent',
            }}
          >
            <span style={{ display: 'grid', placeItems: 'center', flexShrink: 0, width: 18, height: 18, borderRadius: 999, background: 'var(--accent-soft)' }}>
              <Icon name="Plus" size={11} color={Colors.accent} stroke={2.6} />
            </span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Add “{query.trim()}”</span>
          </button>
        )}

        {items.length === 0 && !canCreate && (
          <div style={{ padding: '12px 12px', fontSize: 12.5, color: 'var(--ink-4)', textAlign: 'center' }}>
            {query.trim() ? 'No matching tags' : 'No tags yet — type to create one'}
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(menu, document.body)
    : menu;
}

function TicketRightPanel({ ticket, onReload }: RightPanelProps) {
  const { agentMap, toast, tickets, isInternalSpace, isAssignmentOnly } = useApp();
  const { user, org } = useAuth();
  const { sidePanelStacks } = useResponsive();
  // Employee requester (SK People) vs contact (SK CRM) — drives the panel label.
  const internalSpace = isInternalSpace((ticket as any).spaceId);

  // The mapped ticket carries a placeholder customer.tickets count (0) because
  // a single ticket payload can't know how many others the same person has.
  // Derive it live from the loaded tickets so the panel reflects reality.
  const customerTicketCount = tickets.filter(
    (x: any) => x.customer?.email === ticket.customer.email,
  ).length;

  // The current user as an assignable choice — so tickets can always be
  // self-assigned even before any other agents are registered.
  const selfAgent = user?.id
    ? {
        platformUserId: user.id,
        displayName: ([user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || 'Me') + ' (me)',
      }
    : null;
  const statusTone = ({ open: 'info', pending: 'warn', resolved: 'success', closed: 'neutral' } as any)[ticket.status] ?? 'neutral';
  const priorityTone = ({ urgent: 'danger', high: 'danger', medium: 'warn', low: 'neutral' } as any)[ticket.priority] ?? 'neutral';

  const [showStatusDD, setShowStatusDD] = useState(false);
  const [showPriorityDD, setShowPriorityDD] = useState(false);
  const [showAssigneeDD, setShowAssigneeDD] = useState(false);
  const [agentsList, setAgentsList] = useState<any[]>([]);
  const assigneeBtnRef = useRef<any>(null);
  const [assigneePos, setAssigneePos] = useState<{ top: number; right: number } | null>(null);

  // Tag editor: a dialog-driven add/edit card + a delete→confirm→success flow.
  const [tagDialog, setTagDialog] = useState<{ mode: 'add' | 'edit'; value: string; original?: string } | null>(null);
  const [tagDeleteTarget, setTagDeleteTarget] = useState<string | null>(null);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagSuccess, setTagSuccess] = useState<string | null>(null);
  // Inline tag picker dropdown (web) — a searchable combobox that adds tags in
  // place, so the user never leaves the ticket. `tagQuery` is the search text.
  const [showTagDD, setShowTagDD] = useState(false);
  const [tagQuery, setTagQuery] = useState('');
  const [tagDDPos, setTagDDPos] = useState<{ top: number; left: number } | null>(null);
  const tagInputRef = useRef<any>(null);
  // Premium customer card hover — a gentle lift + accent-tinted ring on web.
  const [custHover, setCustHover] = useState(false);

  // Click-outside to close the status/priority/assignee menus — via a document
  // listener (NOT an overlay), so it never intercepts the option click. Clicks
  // landing INSIDE a dropdown wrapper (tagged `data-dd-keep`) are ignored, so
  // the same click that opens a menu can never also close it — this removes any
  // dependence on React effect-flush timing.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!(showStatusDD || showPriorityDD || showAssigneeDD || showTagDD)) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && typeof t.closest === 'function' && t.closest('[data-dd-keep]')) return;
      setShowStatusDD(false); setShowPriorityDD(false); setShowAssigneeDD(false);
      setShowTagDD(false); setTagQuery('');
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [showStatusDD, showPriorityDD, showAssigneeDD, showTagDD]);

  const handleStatusChange = async (newStatus: string) => {
    setShowStatusDD(false);
    try {
      await ticketsService.updateStatus(ticket._apiId, newStatus);
      onReload();
    } catch (e) {
      toast('Failed to update status', 'danger');
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    setShowPriorityDD(false);
    try {
      await apiClient.patch(TICKETS.update(ticket._apiId), { priority: newPriority });
      onReload();
    } catch (e) {
      toast('Failed to update priority', 'danger');
    }
  };

  // Build the assignee picker from BOTH registered ticket agents AND active org
  // members. An invited teammate becomes an org member the moment they accept,
  // but isn't a ticket "agent" until they're registered — so without merging in
  // members, freshly-invited agents would never appear here. Members who aren't
  // yet agents are flagged so handleAssign can register them on first assign.
  const memberName = (m: any) =>
    [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ').trim()
    || (m.user?.email || '').split('@')[0]
    || m.user_id;

  const openAssignee = async () => {
    // Capture the trigger's viewport rect so the fixed-position menu anchors to
    // it (right edge aligned, growing left over the main area). Done before the
    // await so the menu can position immediately.
    if (assigneeBtnRef.current?.getBoundingClientRect) {
      const r = assigneeBtnRef.current.getBoundingClientRect();
      setAssigneePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    }
    setShowAssigneeDD(true);
    const [agentsRes, membersRes] = await Promise.allSettled([
      agentsService.list(),
      org?.id ? apiClient.get(PLATFORM_ORG.members(org.id)) : Promise.resolve(null),
    ]);

    let agents: any[] = [];
    if (agentsRes.status === 'fulfilled') {
      const raw = (agentsRes.value as any)?.data || agentsRes.value || [];
      agents = Array.isArray(raw) ? raw : [];
    }
    const haveIds = new Set(agents.map((a: any) => a.platformUserId).filter(Boolean));

    // Active org members who aren't registered agents yet → assignable, will be
    // auto-registered on assign.
    let members: any[] = [];
    if (membersRes.status === 'fulfilled' && membersRes.value) {
      const raw = (membersRes.value as any)?.data || membersRes.value || [];
      members = (Array.isArray(raw) ? raw : [])
        .filter((m: any) => (m.status ? m.status === 'active' : true) && m.user_id && !haveIds.has(m.user_id))
        .map((m: any) => ({
          platformUserId: m.user_id,
          displayName: memberName(m),
          email: m.user?.email,
          _needsRegister: true,
        }));
    }

    let list = [...agents, ...members];
    // Always offer "(me)" if the current user isn't already covered.
    if (selfAgent && !list.some((a: any) => a.platformUserId === selfAgent.platformUserId)) {
      list = [selfAgent, ...list];
    }
    setAgentsList(list);
  };

  const handleAssign = async (agent: any) => {
    setShowAssigneeDD(false);
    const assign = () => ticketsService.assign(ticket._apiId, agent.platformUserId);
    // Register an org member as a ticket agent (idempotent — 409 = already one).
    const register = () =>
      agentsService.create({
        platformUserId: agent.platformUserId,
        displayName: (agent.displayName || agent.name || agent.email || 'Agent').replace(/\s*\(me\)$/, ''),
        email: agent.email,
      } as any).catch((err: any) => { if (err?.response?.status !== 409) throw err; });
    try {
      await assign();
      onReload();
    } catch (e: any) {
      // Backend rejects assignees that aren't registered active agents. Members
      // picked from the org list (or the owner self-assigning the first time)
      // hit this — register them once, then retry.
      const notMember = e?.response?.status === 403 || e?.response?.data?.error === 'MEMBER_NOT_FOUND';
      if (notMember && agent.platformUserId) {
        try {
          await register();
          await assign();
          onReload();
          return;
        } catch (e2: any) {
          toast(e2?.response?.data?.message || 'Failed to assign agent', 'danger');
          return;
        }
      }
      toast(e?.response?.data?.message || 'Failed to assign agent', 'danger');
    }
  };

  const handleUnassign = async () => {
    setShowAssigneeDD(false);
    try {
      await ticketsService.assign(ticket._apiId, undefined);
      onReload();
    } catch (e) {
      toast('Failed to unassign', 'danger');
    }
  };

  // Persist a new tag list to the ticket. Single source of truth for the
  // add / edit / delete handlers below — all go through PATCH /tickets/:id.
  const persistTags = async (newTags: string[]) => {
    await apiClient.patch(TICKETS.update(ticket._apiId), { tags: newTags });
    onReload();
  };

  // Add or edit, from the tag dialog card.
  const submitTagDialog = async () => {
    if (!tagDialog) return;
    const value = tagDialog.value.trim();
    if (!value) { setTagDialog(null); return; }
    const existing = ticket.tags || [];
    // Guard against duplicates (ignoring the tag being edited).
    if (existing.some(t => t === value && t !== tagDialog.original)) {
      toast('That tag already exists.', 'warn'); return;
    }
    const newTags = tagDialog.mode === 'edit'
      ? existing.map(t => (t === tagDialog.original ? value : t))
      : [...existing, value];
    setTagBusy(true);
    try {
      await persistTags(newTags);
      setTagDialog(null);
      setTagSuccess(tagDialog.mode === 'edit' ? `Tag updated to "${value}".` : `Tag "${value}" added.`);
    } catch {
      toast('Failed to save tag', 'danger');
    } finally {
      setTagBusy(false);
    }
  };

  // Inline toggle — used by the dropdown combobox. Adds the tag if it's not on
  // the ticket, removes it if it is. Everything happens in place (no modal, no
  // success card); the picker stays open so several can be toggled in a row.
  const toggleTag = async (raw: string) => {
    const value = (raw || '').trim();
    if (!value) return;
    const existing = ticket.tags || [];
    const isApplied = existing.some(t => t.toLowerCase() === value.toLowerCase());
    setTagBusy(true);
    try {
      const next = isApplied
        ? existing.filter(t => t.toLowerCase() !== value.toLowerCase())
        : [...existing, value];
      await persistTags(next);
      setTagQuery('');
      if (Platform.OS === 'web' && tagInputRef.current?.focus) tagInputRef.current.focus();
    } catch {
      toast(isApplied ? 'Failed to remove tag' : 'Failed to add tag', 'danger');
    } finally {
      setTagBusy(false);
    }
  };

  // Delete, after confirmation.
  const confirmDeleteTag = async () => {
    if (!tagDeleteTarget) return;
    setTagBusy(true);
    try {
      await persistTags((ticket.tags || []).filter(t => t !== tagDeleteTarget));
      const removed = tagDeleteTarget;
      setTagDeleteTarget(null);
      setTagSuccess(`Tag "${removed}" removed.`);
    } catch {
      toast('Failed to remove tag', 'danger');
    } finally {
      setTagBusy(false);
    }
  };

  const ddStyle: any = {
    position: 'absolute', top: '100%', left: 0, zIndex: 999,
    background: 'var(--card)', border: '1px solid var(--line-strong)',
    borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    minWidth: 150, padding: '4px 0',
  };
  const ddItemStyle: any = {
    padding: '7px 14px', cursor: 'pointer', fontSize: 13,
    color: 'var(--ink)', display: 'block', width: '100%',
    background: 'none', border: 'none', textAlign: 'left',
  };

  // Tag picker items: the full universe of tags (this ticket's own tags PLUS
  // every tag used across the user's other tickets), filtered by query. Each
  // item is flagged `applied` so the dropdown can show — and toggle — the tags
  // already on this ticket instead of hiding them.
  const appliedTags = new Set((ticket.tags || []).map(t => t.toLowerCase()));
  const tagQ = tagQuery.trim().toLowerCase();
  const tagUniverse = Array.from(
    new Set([
      ...(ticket.tags || []),
      ...tickets.flatMap((x: any) => x.tags || []).map((t: any) => String(t)),
    ]),
  );
  const tagItems = tagUniverse
    .filter(t => !tagQ || t.toLowerCase().includes(tagQ))
    .map(t => ({ tag: t, applied: appliedTags.has(t.toLowerCase()) }))
    // Applied tags float to the top, then alphabetical.
    .sort((a, b) => (Number(b.applied) - Number(a.applied)) || a.tag.localeCompare(b.tag))
    .slice(0, 12);
  // Offer "create" only when the query matches no existing tag at all.
  const canCreateTag =
    tagQ.length > 0 && !tagUniverse.some(t => t.toLowerCase() === tagQ);

  return (
    <>
    <View style={[styles.rightPanel, sidePanelStacks && styles.rightPanelMobile]}>
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Requester section — EMPLOYEE (SK People) vs CUSTOMER (SK CRM).
            Wrapped in a soft card lockup for a premium, contained feel. */}
        <Text style={styles.panelSectionLabel}>{internalSpace ? 'EMPLOYEE' : 'CUSTOMER'}</Text>
        <View
          style={[styles.panelCustomerCard, Platform.OS === 'web' && custHover && styles.panelCustomerCardHover]}
          {...(Platform.OS === 'web'
            ? { onMouseEnter: () => setCustHover(true), onMouseLeave: () => setCustHover(false) }
            : {})}
        >
          <View style={styles.panelCustomerRow}>
            <Avatar
              initials={ticket.customer.initials}
              color={internalSpace ? Colors.internal : ticket.customer.color}
              size="lg"
              style={Platform.OS === 'web'
                ? { boxShadow: `0 0 0 4px ${internalSpace ? 'var(--internal-soft)' : 'var(--accent-soft)'}` } as any
                : undefined}
            />
            <View style={{ flex: 1, marginLeft: 10, minWidth: 0 }}>
              <Text style={styles.panelCustomerName} numberOfLines={1}>{ticket.customer.name}</Text>
              <Text style={styles.panelCustomerEmail} numberOfLines={1}>{ticket.customer.email}</Text>
              <Text style={styles.panelCustomerSub}>
                {internalSpace
                  ? `${(ticket as any).requesterId || 'Employee'} · via SK People`
                  : `${ticket.customer.org} · ${customerTicketCount} ticket${customerTicketCount === 1 ? '' : 's'} · Since ${ticket.customer.since}`}
              </Text>
            </View>
          </View>
        </View>

        {/* DETAILS — the property grid below. */}
        <Text style={[styles.panelSectionLabel, { marginTop: 4 }]}>DETAILS</Text>

        {/* STATUS — clickable dropdown */}
        <PropRow label="STATUS" z={showStatusDD ? 100 : undefined}>
          {Platform.OS === 'web' ? (
            <div data-dd-keep style={{ position: 'relative' }}>
              <button onClick={() => setShowStatusDD(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <Chip label={(() => { const s = (ticket._apiStatus || ticket.status).replace('_', ' '); return s.charAt(0).toUpperCase() + s.slice(1); })()} tone={statusTone} dot size="sm" />
              </button>
              {showStatusDD && (
                <div style={ddStyle}>
                  {/* Only the moves the API allows from here — see lib/ticketStatus. */}
                  {nextStatuses(ticket._apiStatus).map(s => (
                    <button key={s} style={ddItemStyle} onClick={() => handleStatusChange(s)}>
                      {statusMenuLabel(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Chip label={ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)} tone={statusTone} dot size="sm" />
          )}
        </PropRow>

        {/* PRIORITY — clickable dropdown */}
        <PropRow label="PRIORITY" z={showPriorityDD ? 100 : undefined}>
          {Platform.OS === 'web' ? (
            <div data-dd-keep style={{ position: 'relative' }}>
              <button onClick={() => setShowPriorityDD(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <Chip label={ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} tone={priorityTone} dot size="sm" />
              </button>
              {showPriorityDD && (
                <div style={ddStyle}>
                  {PRIORITY_OPTIONS.map(p => (
                    <button key={p} style={ddItemStyle} onClick={() => handlePriorityChange(p)}>{p}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Chip label={ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} tone={priorityTone} dot size="sm" />
          )}
        </PropRow>

        {/*
          ASSIGNEE — clickable dropdown.

          SKTICK-DB-001 §2.2: an assignment-only user CANNOT reassign, and no
          assignee picker is rendered for them. This is not only about the
          action failing. Since WP-1, assignment is a disclosure primitive —
          the picker would enumerate agents, and for an outlet manager that is
          a roster of people they have no reason to see. The server refuses the
          write regardless; this keeps the affordance from existing.
        */}
        <PropRow label="ASSIGNEE" z={showAssigneeDD ? 100 : undefined}>
          {isAssignmentOnly === true ? (
            ticket.assignee ? (
              <View style={styles.assigneeRow}>
                <Avatar initials={ticket.assignee.initials} color={ticket.assignee.color} size="sm" />
                <Text style={styles.panelPropText}>{ticket.assignee.name}</Text>
              </View>
            ) : (
              <Text style={[styles.panelPropText, { color: Colors.ink4 }]}>Unassigned</Text>
            )
          ) : Platform.OS === 'web' ? (
            <div data-dd-keep style={{ position: 'relative' }}>
              <button ref={assigneeBtnRef} onClick={openAssignee} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                {ticket.assignee ? (
                  <>
                    <Avatar initials={ticket.assignee.initials} color={ticket.assignee.color} size="sm" />
                    <Text style={[styles.panelPropText, { fontWeight: FontWeight.medium }]}>{ticket.assignee.name}</Text>
                  </>
                ) : (
                  <Text style={[styles.panelPropText, { color: Colors.ink4 }]}>Unassigned</Text>
                )}
              </button>
              {showAssigneeDD && (
                <AssigneeMenu
                  agents={agentsList}
                  currentAssigneeId={ticket.assignedTo}
                  currentUserId={user?.id}
                  onPick={handleAssign}
                  onUnassign={handleUnassign}
                  pos={assigneePos}
                />
              )}
            </div>
          ) : (
            ticket.assignee ? (
              <View style={styles.assigneeRow}>
                <Avatar initials={ticket.assignee.initials} color={ticket.assignee.color} size="sm" />
                <Text style={styles.panelPropText}>{ticket.assignee.name}</Text>
              </View>
            ) : (
              <Text style={[styles.panelPropText, { color: Colors.ink4 }]}>Unassigned</Text>
            )
          )}
        </PropRow>

        <PropRow label="CHANNEL">
          <View style={styles.channelRow}>
            <ChannelIcon channel={ticket.channel} size={12} />
            <Chip
              label={ticket.channel.charAt(0).toUpperCase() + ticket.channel.slice(1)}
              tone="default"
              size="sm"
            />
          </View>
        </PropRow>

        {/*
          SKTICK-DB-001 Surface C — the node this ticket belongs to. A LABEL,
          never a link and never a breadcrumb: SK Tickets does not resolve the
          SK Platform hierarchy and must not appear to.
        */}
        {!!ticket.nodeId && (
          <PropRow label="OUTLET">
            <NodeLabel nodeId={ticket.nodeId} nodeLabel={ticket.nodeLabel} />
          </PropRow>
        )}

        <PropRow label="CATEGORY">
          <Chip label={ticket.category} tone="accent" dot size="sm" />
        </PropRow>

        {/* TAGS — chips carry a delete affordance; "Add" opens an inline,
            searchable picker (web) that adds tags without leaving the ticket. */}
        <PropRow label="TAGS" z={showTagDD ? 100 : undefined}>
          <View style={styles.tagsRow}>
            {(ticket.tags || []).map(tag => (
              <TagChip key={tag} tag={tag} onDelete={() => setTagDeleteTarget(tag)} />
            ))}
            {Platform.OS === 'web' ? (
              <div data-dd-keep style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  onClick={(e: any) => {
                    if (showTagDD) { setShowTagDD(false); setTagQuery(''); return; }
                    const r = e.currentTarget.getBoundingClientRect();
                    const W = 248;
                    const left = Math.max(8, Math.min(r.left, window.innerWidth - W - 8));
                    setTagDDPos({ top: r.bottom + 8, left });
                    setTagQuery('');
                    setShowTagDD(true);
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, height: 28,
                    paddingLeft: 12, paddingRight: 13, border: '1px solid var(--accent-line)',
                    borderRadius: 999, cursor: 'pointer', boxShadow: 'var(--shadow-sm)',
                    background: showTagDD ? 'var(--accent-line)' : 'var(--accent-soft)',
                    color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600, letterSpacing: 0.1,
                    transition: 'background-color 140ms ease, box-shadow 140ms ease',
                  }}
                >
                  <Icon name="Plus" size={11} color={Colors.accent} stroke={2.4} />
                  Add
                </button>
                {showTagDD && (
                  <TagPickerDropdown
                    pos={tagDDPos}
                    query={tagQuery}
                    onQueryChange={setTagQuery}
                    items={tagItems}
                    canCreate={canCreateTag}
                    busy={tagBusy}
                    inputRef={tagInputRef}
                    onPick={toggleTag}
                    onClose={() => { setShowTagDD(false); setTagQuery(''); }}
                  />
                )}
              </div>
            ) : (
              <TouchableOpacity style={styles.tagAddBtn} onPress={() => setTagDialog({ mode: 'add', value: '' })}>
                <Icon name="Plus" size={11} color={Colors.accent} stroke={2.4} />
                <Text style={styles.tagAddText}>Add</Text>
              </TouchableOpacity>
            )}
          </View>
        </PropRow>

        <PropRow label="WATCHERS">
          <WatchersRow ticket={ticket} onReload={onReload} />
        </PropRow>

        <PropRow label="REPLY-TO">
          <Text style={[styles.monoText, { flexShrink: 1 }]} numberOfLines={3}>{ticket.replyTo}</Text>
        </PropRow>

        {/*
          SKTICK-DB-001 Surface D — SLA state.

          This used to render slaText(ticket.slaMinutes), a countdown derived
          here from first_response_due_at and the wall clock. That is exactly
          the second calculation SKTICK-CR-001 WP-5 removes: it knows nothing
          about pause, so on a `pending` or `on_hold` ticket it kept counting
          down while the real clock was stopped — disagreeing with the ticket,
          with the escalation sweeper and with SK HQ Console's overdue count.

          `ticket.sla` is resolved server-side, already net of pause including
          the currently open window. The client renders it and computes
          nothing. SlaDetail also states the paused fact in words, so a stopped
          clock never reads as a stuck screen.

          The fallback keeps pre-WP-5 responses rendering something sensible
          rather than an empty row.
        */}
        {ticket.sla ? (
          <PropRow label="SLA">
            <SlaDetail sla={ticket.sla} reopenCount={ticket.reopenCount} />
          </PropRow>
        ) : (
          <PropRow label="SLA">
            <View style={styles.slaRow}>
              <Icon name="Clock" size={12} color={slaColor(ticket.slaMinutes)} />
              <Text style={[styles.panelPropText, { color: slaColor(ticket.slaMinutes) }]}>
                {slaText(ticket.slaMinutes)}
              </Text>
            </View>
          </PropRow>
        )}
      </ScrollView>
    </View>

      {/* Tag add / edit card */}
      <ConfirmDialog
        visible={tagDialog !== null}
        title={tagDialog?.mode === 'edit' ? 'Edit tag' : 'Add tag'}
        message={tagDialog?.mode === 'edit'
          ? 'Update the label for this tag.'
          : 'Add a tag to organise and filter this ticket.'}
        confirmText={tagDialog?.mode === 'edit' ? 'Save' : 'Add'}
        cancelText="Cancel"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={submitTagDialog}
        onCancel={() => { if (!tagBusy) setTagDialog(null); }}
        onClose={() => { if (!tagBusy) setTagDialog(null); }}
      >
        <TextInput
          style={styles.dialogInput}
          value={tagDialog?.value ?? ''}
          onChangeText={(v) => setTagDialog(d => (d ? { ...d, value: v } : d))}
          placeholder="e.g. billing"
          placeholderTextColor={Colors.ink4}
          autoFocus
          onSubmitEditing={submitTagDialog}
        />
      </ConfirmDialog>

      {/* Tag delete → confirm */}
      <ConfirmDialog
        visible={tagDeleteTarget !== null}
        title="Remove tag?"
        message={tagDeleteTarget ? `The tag "${tagDeleteTarget}" will be removed from this ticket.` : ''}
        confirmText="Remove"
        cancelText="Cancel"
        loading={tagBusy}
        onConfirm={confirmDeleteTag}
        onCancel={() => { if (!tagBusy) setTagDeleteTarget(null); }}
      />

      {/* Tag success */}
      <SuccessDialog
        visible={tagSuccess !== null}
        title="Done"
        message={tagSuccess ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setTagSuccess(null) }}
        onClose={() => setTagSuccess(null)}
      />
    </>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────────────
interface ComposerProps {
  ticket: Ticket;
}

function Composer({ ticket }: ComposerProps) {
  const { postComment, isInternalSpace } = useApp();
  const [mode, setMode] = useState<'public' | 'internal'>('public');
  const [body, setBody] = useState('');
  const [focused, setFocused] = useState(false);

  const isPublic = mode === 'public';
  const isInternal = mode === 'internal';
  // Whether this ticket lives in an INTERNAL space (distinct from the
  // public-reply / internal-note toggle above, which is unchanged).
  const internalSpace = isInternalSpace((ticket as any).spaceId);

  const handleSend = () => {
    if (!body.trim()) return;
    postComment(ticket.id, { type: mode, body });
    setBody('');
  };

  const insertVar = () => {
    setBody(b => b + '{{customer_name}}');
  };

  return (
    <View style={styles.composer}>
      {/* INTERNAL space banner — replies never reach a customer. */}
      {internalSpace && (
        <View style={detailInternalStyles.composerBanner}>
          <View style={detailInternalStyles.composerBannerTag}>
            <Icon name="Lock" size={11} color="#fff" />
            <Text style={detailInternalStyles.composerBannerTagText}>INTERNAL</Text>
          </View>
          <Text style={detailInternalStyles.composerBannerText}>
            You're replying inside an INTERNAL space. Replies reach the employee and space members only — never a customer.
          </Text>
        </View>
      )}
      {/* Mode toggle */}
      <View style={styles.composerToggleWrap}>
        <TouchableOpacity
          style={[styles.composerToggleBtn, isPublic && styles.composerToggleBtnAccentActive]}
          onPress={() => setMode('public')}
          activeOpacity={0.8}
        >
          <Text style={[styles.composerToggleText, isPublic && styles.composerToggleTextActive]}>
            Public reply
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.composerToggleBtn, isInternal && styles.composerToggleBtnWarnActive]}
          onPress={() => setMode('internal')}
          activeOpacity={0.8}
        >
          <Text style={[styles.composerToggleText, isInternal && styles.composerToggleTextActiveInternal]}>
            Internal note
          </Text>
        </TouchableOpacity>
      </View>

      {/* Textarea */}
      <TextInput
        style={[
          styles.composerTextarea,
          isInternal
            ? { borderColor: Colors.warnLine, backgroundColor: Colors.warnSoft }
            : { borderColor: Colors.lineStrong, backgroundColor: Colors.card },
          Platform.OS === 'web' && focused
            ? ({
                borderColor: isInternal ? Colors.warn : Colors.accent,
                boxShadow: `0 0 0 3px ${isInternal ? 'var(--warn-soft)' : 'var(--accent-soft)'}, inset 0 1px 2px rgba(16,24,40,0.04)`,
              } as any)
            : null,
        ]}
        multiline
        placeholder={isInternal ? 'Type your internal note…' : 'Type your reply…'}
        placeholderTextColor={Colors.ink4}
        value={body}
        onChangeText={setBody}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        textAlignVertical="top"
      />

      {/* Toolbar — clean footer with a single, prominent send action. */}
      <View style={styles.composerToolbar}>
        <View style={{ flex: 1 }} />
        <PrimaryButton
          title={isInternal ? 'Add note' : 'Add reply'}
          size="md"
          variant={isInternal ? 'primary' : 'accent'}
          disabled={!body.trim()}
          leftIcon={
            isInternal
              ? <Icon name="Lock" size={14} color={Colors.paper} />
              : <Icon name="Send" size={14} color="#fff" />
          }
          onPress={handleSend}
        />
      </View>
    </View>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────
interface TicketDetailScreenProps {
  id: string;
}

// INTERNAL space partition styling — the header lockup + composer banner.
const detailInternalStyles = StyleSheet.create({
  lockup: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 26, paddingHorizontal: 11, borderRadius: Radii.full, backgroundColor: Colors.internal },
  lockupText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: '#fff', letterSpacing: 0.3 },
  lockupKey: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: 11.5, fontWeight: FontWeight.bold, color: '#fff', opacity: 0.85 },
  composerBanner: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 10, borderRadius: Radii.r2, backgroundColor: Colors.internalSoft, borderWidth: 1, borderColor: Colors.internalLine, borderLeftWidth: 3, borderLeftColor: Colors.internal },
  composerBannerTag: { flexDirection: 'row', alignItems: 'center', gap: 4, height: 20, paddingHorizontal: 8, borderRadius: Radii.full, backgroundColor: Colors.internal },
  composerBannerTagText: { fontSize: 10.5, fontWeight: FontWeight.bold, color: '#fff', letterSpacing: 0.3 },
  composerBannerText: { flex: 1, fontSize: 12, color: Colors.internalInk, lineHeight: 16 },
});

export function TicketDetailScreen({ id }: TicketDetailScreenProps) {
  const { tickets, resolveTicket, toggleDeliveryExpanded, loadTicketComments, reloadTicket, toast, isInternalSpace, spaceById, editComment, deleteComment, isAssignmentOnly } = useApp();
  const { user } = useAuth();
  const { sidePanelStacks, pagePadX } = useResponsive();
  const ticket = tickets.find(t => t.id === id);
  const threadRef = useRef<ScrollView>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [morePos, setMorePos] = useState<{ top: number; right: number } | null>(null);
  // Resolve / re-open: confirm → (API) → success dialog.
  const [resolveConfirm, setResolveConfirm] = useState(false);
  const [resolveMsg, setResolveMsg] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  // Comment edit / delete: edit card + delete→confirm, both → success dialog.
  const [editTarget, setEditTarget] = useState<Comment | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Comment | null>(null);
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState<string | null>(null);

  const openEdit = (c: Comment) => { setEditTarget(c); setEditDraft(c.body || ''); };
  const saveEdit = async () => {
    if (!ticket || !editTarget) return;
    setCommentBusy(true);
    const ok = await editComment(ticket.id, editTarget.id, editDraft);
    setCommentBusy(false);
    if (ok) { setEditTarget(null); setCommentSuccess('Message updated.'); }
  };
  const confirmDelete = async () => {
    if (!ticket || !deleteTarget) return;
    setCommentBusy(true);
    const ok = await deleteComment(ticket.id, deleteTarget.id);
    setCommentBusy(false);
    if (ok) { setDeleteTarget(null); setCommentSuccess('Message deleted.'); }
  };

  // Click-outside closes the portaled ⋯ menu (ignores clicks inside any
  // `data-dd-keep` wrapper so the trigger click can't immediately re-close it).
  useEffect(() => {
    if (Platform.OS !== 'web' || !showMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && typeof t.closest === 'function' && t.closest('[data-dd-keep]')) return;
      setShowMenu(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [showMenu]);

  useEffect(() => {
    if (id) loadTicketComments(id);
  }, [id]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollToEnd({ animated: true });
    }
  }, [ticket?.comments.length]);

  if (!ticket) {
    // Default-deny silence: not-found and no-access render the SAME generic
    // state — it must never confirm a ticket exists or name its space.
    //
    // SKTICK-DB-001 §2.3: for an assignment-only user the copy differs, and
    // the difference matters. The general message mentions "a space you don't
    // have access to", which NAMES A THING THEY MUST NOT BE SHOWN EXISTS. For
    // them the honest and sufficient fact is that the ticket is no longer
    // theirs — most often because it was just unassigned while they had it
    // open. No 403, no retained detail, no mention of spaces.
    const assignmentOnly = isAssignmentOnly === true;
    const body = assignmentOnly
      ? 'This ticket is no longer assigned to you.'
      : "This view is empty for your account. If you were expecting something, it may live in a space you don't have access to.";
    return (
      <ScreenShell>
        <View style={styles.notFound}>
          <View style={{ width: 48, height: 48, borderRadius: Radii.r3, backgroundColor: Colors.paper3, alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <Icon name="Inbox" size={22} color={Colors.ink4} />
          </View>
          <Text style={[styles.notFoundText, { fontWeight: FontWeight.semibold, color: Colors.ink }]}>
            {assignmentOnly ? 'No longer assigned' : 'Nothing to show here'}
          </Text>
          <Text style={{ fontSize: FontSize.md, color: Colors.ink3, marginTop: 6, textAlign: 'center', maxWidth: 360, lineHeight: 20 }}>
            {body}
          </Text>
          <View style={{ marginTop: 16 }}>
            <PrimaryButton
              title={assignmentOnly ? 'Back to my assignments' : 'Back to tickets'}
              size="sm"
              variant="default"
              onPress={() => navigate('/tickets')}
            />
          </View>
        </View>
      </ScreenShell>
    );
  }

  const statusTone = ({ open: 'info', pending: 'warn', resolved: 'success', closed: 'neutral' } as any)[ticket.status] ?? 'neutral';
  const priorityTone = ({ urgent: 'danger', high: 'danger', medium: 'warn', low: 'neutral' } as any)[ticket.priority] ?? 'neutral';

  // Run the resolve/re-open after the user confirms, then show success.
  const handleResolve = async () => {
    const wasResolved = ticket.status === 'resolved';
    setResolving(true);
    try {
      await resolveTicket(ticket.id);
      setResolveConfirm(false);
      setResolveMsg(wasResolved
        ? `Ticket ${ticket.id} has been re-opened.`
        : `Ticket ${ticket.id} has been marked as resolved. A CSAT email goes out in 24h.`);
    } finally {
      setResolving(false);
    }
  };

  // INTERNAL space partition: make the ticket's internal-ness unmistakable.
  const internal = isInternalSpace((ticket as any).spaceId);
  const spaceKey = (spaceById((ticket as any).spaceId)?.key || '').toUpperCase();

  return (
    <ScreenShell>
      <View style={[styles.detailRoot, sidePanelStacks && styles.detailRootStacked]}>
        {/* LEFT PANE */}
        <View style={styles.leftPane}>
          {/* Ticket Header */}
          <View style={[styles.ticketHeader, { paddingHorizontal: pagePadX }]}>
            {/* Row 1: badges + actions */}
            <View style={styles.ticketHeaderRow1}>
              <Text style={styles.ticketId}>{ticket.id}</Text>
              <Text style={styles.dot}>·</Text>
              <Chip
                label={(() => { const s = (ticket._apiStatus || ticket.status).replace('_', ' '); return s.charAt(0).toUpperCase() + s.slice(1); })()}
                tone={statusTone}
                dot
                size="md"
              />
              <View style={styles.channelChipWrap}>
                <ChannelIcon channel={ticket.channel} size={12} />
                <Chip
                  label={ticket.channel.charAt(0).toUpperCase() + ticket.channel.slice(1)}
                  tone="default"
                  size="md"
                />
              </View>
              <Chip
                label={`${ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} priority`}
                tone={priorityTone}
                dot
                size="md"
              />
              <Chip label={ticket.category} tone="accent" dot size="md" />
              {internal && (
                <View style={detailInternalStyles.lockup}>
                  <Icon name="Lock" size={12} color="#fff" />
                  <Text style={detailInternalStyles.lockupText}>INTERNAL</Text>
                  {!!spaceKey && <Text style={detailInternalStyles.lockupKey}>{spaceKey}</Text>}
                </View>
              )}
              <View style={{ flex: 1 }} />
              <PrimaryButton
                title="Refresh"
                size="sm"
                variant="ghost"
                leftIcon={<Icon name="Refresh" size={13} color={Colors.ink2} />}
                onPress={() => loadTicketComments(ticket.id)}
              />
              <PrimaryButton
                title={ticket.status === 'resolved' ? 'Re-open' : 'Resolve'}
                size="sm"
                variant="primary"
                disabled={resolving}
                onPress={() => setResolveConfirm(true)}
              />
              <div data-dd-keep style={{ position: 'relative' }}>
                <PrimaryButton
                  size="icon"
                  variant="ghost"
                  leftIcon={<Icon name="More" size={15} color={Colors.ink2} />}
                  onPress={(e?: any) => {
                    // Anchor a portaled, fixed-position menu to the button so it
                    // floats over the whole window and never gets clipped by the
                    // header / thread overflow.
                    const r = e?.currentTarget?.getBoundingClientRect?.()
                      || (typeof document !== 'undefined' && document.activeElement?.getBoundingClientRect?.());
                    if (r) setMorePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
                    setShowMenu(m => !m);
                  }}
                />
                {showMenu && <MoreMenu pos={morePos} onClose={() => setShowMenu(false)} ticket={ticket} />}
              </div>
            </View>

            {/* Title */}
            <Text style={styles.ticketTitle}>{ticket.subject}</Text>

            {/* Row 3: meta */}
            <View style={styles.ticketHeaderRow3}>
              <Text style={styles.ticketMeta}>Opened {ticket.openedAgo} by</Text>
              <Avatar
                initials={ticket.customer.initials}
                color={ticket.customer.color}
                size="xs"
                style={{ marginLeft: 5, marginRight: 4 }}
              />
              <Text style={styles.ticketMetaName}>{ticket.customer.name}</Text>
              <Text style={styles.ticketMeta}> · {ticket.comments.length} messages</Text>
            </View>
          </View>

          {/* Thread */}
          <ScrollView
            ref={threadRef}
            style={styles.thread}
            contentContainerStyle={[styles.threadContent, { paddingHorizontal: pagePadX }]}
            showsVerticalScrollIndicator
          >
            {/* Opening context — the customer's original request. */}
            <OpeningRequest ticket={ticket} />

            {ticket.comments.map(comment => (
              <CommentItem
                key={comment.id}
                comment={comment}
                ticket={ticket}
                onToggleDelivery={(cid) => toggleDeliveryExpanded(ticket.id, cid)}
                currentUserId={user?.id}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            ))}

            {/* No replies yet → a graceful, centered empty state. */}
            {ticket.comments.length === 0 && <ThreadEmpty ticket={ticket} />}
          </ScrollView>

          {/* Composer */}
          <Composer ticket={ticket} />
        </View>

        {/* RIGHT PANEL */}
        <TicketRightPanel ticket={ticket} onReload={() => reloadTicket(ticket.id)} />
      </View>

      {/* Resolve / re-open — confirm first */}
      <ConfirmDialog
        visible={resolveConfirm}
        title={ticket.status === 'resolved' ? 'Re-open ticket?' : 'Resolve ticket?'}
        message={ticket.status === 'resolved'
          ? `Ticket ${ticket.id} will be moved back to open and re-enter the queue.`
          : `Ticket ${ticket.id} will be marked as resolved. The customer gets a CSAT email in 24h.`}
        confirmText={ticket.status === 'resolved' ? 'Re-open' : 'Resolve'}
        cancelText="Cancel"
        confirmColor={ticket.status === 'resolved' ? Colors.accent : Colors.success}
        loading={resolving}
        onConfirm={handleResolve}
        onCancel={() => { if (!resolving) setResolveConfirm(false); }}
      />

      {/* …then success */}
      <SuccessDialog
        visible={resolveMsg !== null}
        title={ticket.status === 'resolved' ? 'Ticket resolved' : 'Ticket re-opened'}
        message={resolveMsg ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setResolveMsg(null) }}
        onClose={() => setResolveMsg(null)}
      />

      {/* Edit message — inline card (no navigation away) */}
      <ConfirmDialog
        visible={editTarget !== null}
        title="Edit message"
        message="Update your reply. The customer sees the latest version."
        confirmText="Save"
        cancelText="Cancel"
        confirmColor={Colors.accent}
        loading={commentBusy}
        onConfirm={saveEdit}
        onCancel={() => { if (!commentBusy) setEditTarget(null); }}
        onClose={() => { if (!commentBusy) setEditTarget(null); }}
      >
        <TextInput
          style={[styles.dialogInput, { minHeight: 96, textAlignVertical: 'top' }]}
          value={editDraft}
          onChangeText={setEditDraft}
          placeholder="Type your message…"
          placeholderTextColor={Colors.ink4}
          multiline
          autoFocus
        />
      </ConfirmDialog>

      {/* Delete message → confirm */}
      <ConfirmDialog
        visible={deleteTarget !== null}
        title="Delete message?"
        message="This message will be permanently removed from the conversation. This can't be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor={Colors.danger}
        loading={commentBusy}
        onConfirm={confirmDelete}
        onCancel={() => { if (!commentBusy) setDeleteTarget(null); }}
        onClose={() => { if (!commentBusy) setDeleteTarget(null); }}
      />

      {/* Edit / delete success */}
      <SuccessDialog
        visible={commentSuccess !== null}
        title="Done"
        message={commentSuccess ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setCommentSuccess(null) }}
        onClose={() => setCommentSuccess(null)}
      />
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  notFound: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  notFoundText: { fontSize: FontSize.lg, color: Colors.ink3 },

  detailRoot: {
    flex: 1,
    flexDirection: 'row',
  },
  // On mobile the two panes stack vertically; the whole view scrolls.
  detailRootStacked: {
    flexDirection: 'column',
    ...(Platform.OS === 'web' ? { overflowY: 'auto' } as any : {}),
  },

  // ── Left pane
  leftPane: {
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },

  // ── Ticket header
  ticketHeader: {
    paddingTop: 22,
    paddingBottom: 20,
    paddingHorizontal: 30,
    backgroundColor: Colors.card,
    borderBottomWidth: 1,
    borderBottomColor: Colors.line,
    gap: 13,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(180deg, var(--card) 0%, var(--paper) 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 0 rgba(16,24,40,0.02)',
        } as any
      : {}),
  },
  ticketHeaderRow1: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    rowGap: 8,
  },
  ticketId: {
    fontSize: 12.5,
    fontWeight: FontWeight.medium,
    color: Colors.ink3,
    fontFamily: Platform.select({ web: "'IBM Plex Mono', monospace", default: 'IBMPlexMono' }),
  },
  dot: {
    fontSize: 12.5,
    color: Colors.ink4,
    marginHorizontal: 4,
  },
  channelChipWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  ticketTitle: {
    fontSize: 24,
    fontWeight: FontWeight.bold,
    color: Colors.ink,
    letterSpacing: -0.5,
    lineHeight: 31,
  },
  ticketHeaderRow3: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  ticketMeta: {
    fontSize: FontSize.sm,
    color: Colors.ink3,
  },
  ticketMetaName: {
    fontSize: FontSize.sm,
    fontWeight: FontWeight.medium,
    color: Colors.ink2,
  },

  // ── Thread — a soft top-down gradient surface gives the conversation depth
  // and lets the elevated message cards read as floating.
  thread: {
    flex: 1,
    backgroundColor: Colors.paper,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(180deg, var(--paper) 0%, var(--paper-2) 100%)' } as any
      : {}),
  },
  threadContent: {
    flexGrow: 1,
    paddingVertical: 24,
    paddingHorizontal: 28,
    gap: 16,
  },

  // Opening request card — distinct from a normal customer message.
  openingCard: {
    position: 'relative',
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.r5,
    padding: 20,
    paddingLeft: 24,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(158deg, var(--card) 0%, var(--accent-soft) 220%)',
          boxShadow: 'var(--shadow), inset 0 1px 0 rgba(255,255,255,0.6)',
        } as any
      : (Shadow.base as object)),
  },
  openingAccent: {
    position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
    backgroundColor: Colors.accent,
  },
  openingTag: {
    backgroundColor: Colors.accentSoft,
    borderWidth: 1, borderColor: Colors.accentLine,
    borderRadius: Radii.full,
    paddingHorizontal: 8, paddingVertical: 2,
  },
  openingTagText: {
    fontSize: 10.5, fontWeight: FontWeight.bold, color: Colors.accentInk,
    letterSpacing: 0.2,
  },
  openingSubject: {
    fontSize: 14.5, fontWeight: FontWeight.semibold, color: Colors.ink,
    letterSpacing: -0.1,
  },

  // Empty thread state.
  threadEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    gap: 8,
  },
  threadEmptyIcon: {
    width: 52, height: 52, borderRadius: Radii.full,
    backgroundColor: Colors.accentSoft,
    borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center',
    marginBottom: 4,
  },
  threadEmptyTitle: {
    fontSize: 14.5, fontWeight: FontWeight.semibold, color: Colors.ink2,
  },
  threadEmptySub: {
    fontSize: 12.5, color: Colors.ink3, textAlign: 'center',
    maxWidth: 340, lineHeight: 18,
  },

  // ── Comment base
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  commentAuthor: {
    fontSize: 14.5,
    fontWeight: FontWeight.bold,
    color: Colors.ink,
    letterSpacing: -0.2,
  },
  commentMeta: {
    fontSize: 12,
    color: Colors.ink3,
  },
  italic: {
    fontStyle: 'italic',
  },
  commentTime: {
    fontSize: 11.5,
    color: Colors.ink4,
  },
  commentBody: {
    fontSize: 13.5,
    color: Colors.ink,
    lineHeight: 20,
    ...(Platform.OS === 'web' ? { whiteSpace: 'pre-wrap' } as any : {}),
  },
  commentFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },

  // Customer comment
  commentCustomer: {
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.r5,
    padding: 20,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(160deg, var(--card) 0%, var(--paper-2) 150%)',
          boxShadow: 'var(--shadow-sm), inset 0 1px 0 rgba(255,255,255,0.55)',
        } as any
      : (Shadow.sm as object)),
  },

  // Agent public comment — a faint indigo wash sets agent replies apart.
  commentAgent: {
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.accentLine,
    borderRadius: Radii.r5,
    padding: 20,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(160deg, var(--card) 0%, var(--accent-soft) 240%)',
          boxShadow: 'var(--shadow-sm), inset 0 1px 0 rgba(255,255,255,0.55)',
        } as any
      : {}),
  },

  // Internal comment
  commentInternal: {
    backgroundColor: Colors.warnSoft,
    borderWidth: 1,
    borderColor: Colors.warnLine,
    borderLeftWidth: 4,
    borderLeftColor: Colors.warn,
    borderRadius: Radii.r5,
    padding: 20,
  },

  // System bounce comment
  commentBounce: {
    backgroundColor: Colors.warnSoft,
    borderWidth: 1,
    borderColor: Colors.warnLine,
    borderLeftWidth: 4,
    borderLeftColor: Colors.warn,
    borderRadius: Radii.r5,
    padding: 20,
  },
  bounceIconWrap: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.warn,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bounceBody: {
    fontSize: 13.5,
    color: Colors.ink,
    lineHeight: 20,
  },
  bounceCode: {
    fontFamily: Platform.select({ web: "'IBM Plex Mono', monospace", default: 'IBMPlexMono' }),
    fontWeight: FontWeight.semibold,
    fontSize: 13,
  },
  bounceReason: {
    fontSize: 13,
    color: Colors.ink3,
    marginTop: 4,
    lineHeight: 18,
  },
  bounceOk: {
    fontSize: 13,
    color: Colors.ink3,
    marginTop: 4,
    lineHeight: 18,
  },

  // Delivery badge
  deliveryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 4,
    paddingVertical: 2,
    backgroundColor: 'transparent',
  },
  deliveryBadgeText: {
    fontSize: 12,
    fontWeight: FontWeight.medium,
  },

  // Delivery panel
  deliveryPanel: {
    marginTop: 10,
    backgroundColor: Colors.paper2,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.r3,
    padding: 10,
    paddingHorizontal: 14,
  },
  deliveryPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  deliveryPanelSummary: {
    fontSize: 12.5,
    color: Colors.ink2,
    fontWeight: FontWeight.medium,
  },
  deliveryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    gap: 8,
  },
  deliveryEmail: {
    flex: 1,
    fontSize: 12.5,
    color: Colors.ink2,
    fontFamily: Platform.select({ web: "'IBM Plex Mono', monospace", default: 'IBMPlexMono' }),
  },
  deliveryRowStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  deliveryRowStatusText: {
    fontSize: 12,
    fontWeight: FontWeight.medium,
  },
  deliveryPanelFooter: {
    marginTop: 8,
    alignItems: 'flex-start',
  },

  // ── Composer
  composer: {
    backgroundColor: Colors.card,
    borderTopWidth: 1,
    borderTopColor: Colors.line,
    paddingTop: 14,
    paddingHorizontal: 18,
    paddingBottom: 16,
    gap: 10,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(0deg, var(--card) 0%, var(--paper) 100%)',
          boxShadow: '0 -8px 24px -20px rgba(16,24,40,0.18)',
        } as any
      : {}),
  },
  composerToggleWrap: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    backgroundColor: Colors.paper2,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.full,
    padding: 3,
    gap: 2,
    ...(Shadow.sm as object),
  },
  composerToggleBtn: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: Radii.full,
    backgroundColor: 'transparent',
  },
  composerToggleBtnAccentActive: {
    backgroundColor: Colors.accent,
    ...(Platform.OS === 'web' ? { boxShadow: '0 1px 6px -1px rgba(79,70,229,0.5)' } as any : (Shadow.sm as object)),
  },
  composerToggleBtnWarnActive: {
    backgroundColor: Colors.warn,
    ...(Platform.OS === 'web' ? { boxShadow: '0 1px 6px -1px rgba(217,119,6,0.5)' } as any : (Shadow.sm as object)),
  },
  composerToggleText: {
    fontSize: FontSize.sm,
    fontWeight: FontWeight.medium,
    color: Colors.ink3,
  },
  composerToggleTextActive: {
    color: Colors.white,
  },
  composerToggleTextActiveInternal: {
    color: Colors.white,
  },
  composerSubtitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  composerSubtitleText: {
    fontSize: 12,
    fontWeight: FontWeight.medium,
  },
  internalSubRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  recipientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  recipientLabel: {
    fontSize: FontSize.sm,
    color: Colors.ink3,
  },
  composerTextarea: {
    borderWidth: 1,
    borderRadius: Radii.r4,
    padding: 15,
    fontSize: FontSize.md,
    color: Colors.ink,
    minHeight: 68,
    lineHeight: 21,
    ...(Platform.OS === 'web'
      ? {
          outlineStyle: 'none', resize: 'vertical',
          boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.04)',
          transitionProperty: 'border-color, box-shadow',
          transitionDuration: '160ms',
          transitionTimingFunction: 'ease',
        } as any
      : {}),
  },
  composerToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  composerHint: {
    fontSize: 11.5,
    color: Colors.ink4,
    marginRight: 8,
  },

  // ── Right panel — a slightly wider, premium properties rail. Soft paper
  // backdrop + an inset shadow on its leading edge give it real depth without
  // taking over the whole width.
  rightPanel: {
    width: 450,
    backgroundColor: Colors.paper,
    borderLeftWidth: 1,
    borderLeftColor: Colors.line,
    paddingVertical: 20,
    paddingHorizontal: 22,
    ...(Platform.OS === 'web'
      ? { boxShadow: 'inset 10px 0 24px -18px rgba(16,24,40,0.18)' } as any
      : {}),
  },
  // Mobile: the properties panel drops below the conversation, full width.
  rightPanelMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderTopWidth: 1,
    borderTopColor: Colors.line,
    ...(Platform.OS === 'web' ? { boxShadow: 'none' } as any : {}),
  },
  panelSectionLabel: {
    fontSize: 10.5,
    fontWeight: FontWeight.bold,
    color: Colors.ink4,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 10,
  },
  // Premium customer lockup card — a soft top-down gradient surface with an
  // inset highlight on web gives it depth; it lifts gently on hover.
  panelCustomerCard: {
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.r5,
    padding: 16,
    marginBottom: 18,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(158deg, var(--card) 0%, var(--paper-2) 140%)',
          boxShadow: 'var(--shadow), inset 0 1px 0 rgba(255,255,255,0.65)',
          transitionProperty: 'transform, box-shadow, border-color',
          transitionDuration: '220ms',
          transitionTimingFunction: 'cubic-bezier(.2,.8,.2,1)',
        } as any
      : (Shadow.base as object)),
  },
  panelCustomerCardHover: {
    ...(Platform.OS === 'web'
      ? {
          transform: 'translateY(-2px)',
          boxShadow: 'var(--shadow-md), inset 0 1px 0 rgba(255,255,255,0.7)',
          borderColor: Colors.accentLine,
        } as any
      : {}),
  },
  panelCustomerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  panelCustomerName: {
    fontSize: FontSize.lg,
    fontWeight: FontWeight.semibold,
    color: Colors.ink,
    letterSpacing: -0.2,
  },
  panelCustomerEmail: {
    fontSize: 11.5,
    color: Colors.ink3,
    fontFamily: Platform.select({ web: "'IBM Plex Mono', monospace", default: 'IBMPlexMono' }),
    marginTop: 1,
  },
  panelCustomerSub: {
    fontSize: 11.5,
    color: Colors.ink3,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.line,
    lineHeight: 16,
  },
  propRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: Colors.line,
  },
  propLabel: {
    width: 92,
    flexShrink: 0,
    fontSize: 11,
    color: Colors.ink4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: FontWeight.semibold,
    paddingTop: 3,
  },
  propValue: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
  },
  panelPropText: {
    fontSize: 13,
    color: Colors.ink,
  },
  monoText: {
    fontSize: 11.5,
    color: Colors.ink2,
    fontFamily: Platform.select({ web: "'IBM Plex Mono', monospace", default: 'IBMPlexMono' }),
    flexShrink: 1,
    lineHeight: 16,
  },
  assigneeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  channelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    // Fill the property column so flexWrap actually wraps within the rail
    // instead of the chips overflowing off the right edge.
    width: '100%',
    minWidth: 0,
  },
  // A tag chip with inline edit + delete affordances. Shrinks so long values
  // never push siblings off the rail.
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    maxWidth: '100%',
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.full,
    paddingLeft: 10,
    paddingRight: 8,
    paddingVertical: 5,
    ...(Shadow.sm as object),
  },
  tagChipText: {
    fontSize: 12,
    color: Colors.ink2,
    fontWeight: FontWeight.medium,
    flexShrink: 1,
    maxWidth: 120,
  },
  // Premium "+ Add" pill — subtle indigo fill, accent ring, lifts on hover.
  tagAddBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    backgroundColor: Colors.accentSoft,
    borderWidth: 1,
    borderColor: Colors.accentLine,
    borderRadius: Radii.full,
    paddingHorizontal: 12,
    paddingVertical: 5,
    ...(Shadow.sm as object),
    ...(Platform.OS === 'web'
      ? { cursor: 'pointer', transitionProperty: 'box-shadow, background-color', transitionDuration: '140ms' } as any
      : {}),
  },
  tagAddText: {
    fontSize: 12,
    color: Colors.accentInk,
    fontWeight: FontWeight.semibold,
    letterSpacing: 0.1,
  },
  // Watcher picker rows inside the add-watcher card.
  watcherPickRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: Radii.r2,
  },
  watcherPickName: {
    fontSize: 13,
    fontWeight: FontWeight.semibold,
    color: Colors.ink,
  },
  watcherPickEmail: {
    fontSize: 11,
    color: Colors.ink4,
  },
  // Shared text input used inside the add/edit dialog cards.
  dialogInput: {
    borderWidth: 1,
    borderColor: Colors.lineStrong,
    borderRadius: Radii.r3,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: FontSize.md,
    color: Colors.ink,
    backgroundColor: Colors.card,
    marginTop: 4,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } as any : {}),
  },
  slaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
});
